import { ArrowLeft, Save } from 'lucide-react';
import { Suspense, lazy, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

const TipTapEditor = lazy(() =>
  import('@/components/template-editor/TipTapEditor').then((m) => ({ default: m.TipTapEditor })),
);

interface TemplateDetailResponse {
  id: string | null;
  templateKey: string;
  channel: 'email' | 'sms' | 'whatsapp';
  locale: string;
  subject: string | null;
  body: string;
  isOverride: boolean;
  version: number;
  allowedFields: string[];
  versionHistory: Array<{
    id: string;
    version: number;
    createdAt: string;
    createdByMembershipId: string | null;
  }>;
}

export function TemplateEditPage() {
  const { key, channel, locale } = useParams<{ key: string; channel: string; locale: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<TemplateDetailResponse | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (!key || !channel || !locale) return;
    fetch(`/api/notification-templates/${encodeURIComponent(key)}/${channel}/${locale}`, {
      credentials: 'include',
    })
      .then(async (res) => {
        if (!res.ok) {
          setError(`Failed to load (HTTP ${res.status})`);
          return;
        }
        const body = (await res.json()) as TemplateDetailResponse;
        setData(body);
        setSubject(body.subject ?? '');
        setBody(body.body);
      })
      .catch(() => setError('Network error'));
  }, [key, channel, locale]);

  async function handleSave() {
    if (!data) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/notification-templates', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          templateKey: data.templateKey,
          channel: data.channel,
          locale: data.locale,
          subject: subject || null,
          body,
          whatsappTemplateName: null,
          whatsappMetaCategory: null,
          whatsappApprovalStatus: null,
          whatsappApprovalNotes: null,
        }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        setError((errBody as { error?: string }).error ?? `Save failed (HTTP ${res.status})`);
        return;
      }
      setSavedAt(Date.now());
    } catch {
      setError('Network error saving');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6 px-4 py-6 md:px-8">
      <div className="flex items-center gap-3">
        <Link
          to="/communications/templates"
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground press"
        >
          <ArrowLeft className="size-4" strokeWidth={1.75} />
          Back to templates
        </Link>
      </div>

      <header>
        <h1 className="font-heading text-2xl font-medium tracking-tight">
          {data ? data.templateKey : 'Loading…'}
        </h1>
        {data && (
          <p className="mt-1 text-sm text-muted-foreground">
            {data.channel} · {data.locale}{' '}
            {data.isOverride && (
              <span className="ml-2 inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                Customised · v<span className="num">{data.version}</span>
              </span>
            )}
          </p>
        )}
      </header>

      {error && (
        <p className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </p>
      )}

      {data && (
        <div className="space-y-5">
          {data.channel === 'email' && (
            <div>
              <label
                htmlFor="tpl-subject"
                className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
              >
                Subject
              </label>
              <input
                id="tpl-subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="e.g. Your booking is confirmed"
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
          )}

          <div>
            <label className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              Body
            </label>
            {data.channel === 'email' ? (
              <Suspense
                fallback={<div className="py-8 text-sm text-muted-foreground">Loading editor…</div>}
              >
                <TipTapEditor
                  value={body}
                  onChange={setBody}
                  allowedFields={data.allowedFields}
                  ariaLabel="Email body"
                />
              </Suspense>
            ) : (
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={8}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            )}
            <p className="mt-2 text-xs text-muted-foreground">
              Available variables:{' '}
              {data.allowedFields
                .map((f) => (
                  <code key={f} className="font-mono text-[11px]">
                    {`{{ ${f} }}`}
                  </code>
                ))
                .reduce<React.ReactNode[]>((acc, el, i) => {
                  if (i > 0) acc.push(<span key={`sep-${i}`}>, </span>);
                  acc.push(el);
                  return acc;
                }, [])}
            </p>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-xs text-muted-foreground">
              {savedAt && (
                <span className="text-success">
                  Saved · v{data.version + 1}. Refresh to see new version.
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate('/communications/templates')}
                className="rounded-md border border-border px-4 py-2 text-sm hover:bg-secondary press"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleSave()}
                disabled={saving}
                className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press disabled:opacity-50"
              >
                <Save className="size-4" strokeWidth={1.75} />
                {saving ? 'Saving…' : 'Save new version'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
